import * as React from 'react';
import { Star } from 'lucide-react';
import Link from 'next/link';

import type { ID } from '@nutralab/api';
import { Button, Heading, Icon, Text } from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import styles from './ReviewCard.module.scss';

import { getOfferPath } from '@/shared/utils';

interface ReviewCardProps extends React.HTMLAttributes<HTMLDivElement> {
    offerId: ID;
    imageSrc: string;
    title: string;
    authorName: string;
    description: string;
    rating: number;
    learnMoreLabel: string;
}

/** One featured review: product image, rating, text and a link to the offer. */
export function ReviewCard({
    offerId,
    imageSrc,
    title,
    authorName,
    description,
    rating,
    learnMoreLabel,
    className,
    ...props
}: ReviewCardProps): React.ReactElement {
    return (
        <div className={classNames(styles['review-card'], className)} {...props}>
            <div className={styles['image-wrapper']}>
                <img src={imageSrc} alt={title} loading="lazy" />
            </div>

            <div className={styles.content}>
                <div className={classNames(styles.rating, 'mb-4')}>
                    <Icon icon={Star} size="xl" className={styles['rating-icon']} />
                    <Text as="span" size="md" weight="medium">
                        {rating}
                    </Text>
                </div>

                <Heading className="mb-2" size="xl" weight="semibold">
                    {title}
                </Heading>

                <Text className="mb-4" size="md" color="muted">
                    {authorName}
                </Text>

                <Text size="lg" lineClamp={3}>
                    {description}
                </Text>

                <Button asChild className="mt-4" variant="outlined" size="sm">
                    <Link href={getOfferPath({ id: offerId })}>{learnMoreLabel}</Link>
                </Button>
            </div>
        </div>
    );
}
